import { PlanTypesEnum } from '@ctct/domain-layer';

/**
 * Plan-restructure experiment (CPD-25373) expanded comparison table content.
 *
 * Cells that differ from the default `PricingContentStrategy` are marked with a
 * "PLAN-RESTRUCTURE" comment; every other row mirrors the current copy so the
 * variant is a drop-in replacement for the experimental cohort.
 * Source of truth: yellow-highlighted rows in
 * https://docs.google.com/spreadsheets/d/1E1maAuM9IrIs6S0mQrpXZC9CkgLk0JKDp1RHJZZNWGg
 */
export default {
  headers: {
    plans: [
      {
        id: PlanTypesEnum.Bronze,
        name: 'Lite',
        prePrice: 'Starting at',
        afterPrice: '/month*',
      },
      {
        id: PlanTypesEnum.Silver,
        name: 'Standard',
        prePrice: 'Starting at',
        afterPrice: '/month*',
      },
      {
        id: PlanTypesEnum.Gold,
        name: 'Premium',
        prePrice: 'Starting at',
        afterPrice: '/month*',
      },
    ],
  },
  groups: [
    {
      id: 'platformManagement',
      name: 'Platform Management',
      features: [
        {
          id: 'platformManagement-users',
          feature: {
            name: 'Users',
            tooltipLabel: 'Number of users that work in the same account with different roles and permissions.',
          },
          parameters: [
            { id: '1', value: '2 users' },
            { id: '2', value: '3 users' },
            { id: '3', value: 'Unlimited' },
          ],
        },
        {
          id: 'platformManagement-monthlyEmailSends',
          feature: {
            name: 'Monthly email sends',
            // PLAN-RESTRUCTURE: Lite send multiplier drops from 10x to 5x.
            tooltipLabel: 'Amount of emails included in your plan each month.',
            tooltipLinkText: 'Overage fees of $0.002 per email apply.',
            tooltipLinkUrl:
              'https://knowledgebase.constantcontact.com/email-digital-marketing/articles/KnowledgeBase/46046-Constant-Contacts-email-send-allowance-and-overage-fee?lang=en_US',
          },
          parameters: [
            { id: '1', value: '5x your number of contacts' },
            { id: '2', value: '12x your number of contacts' },
            { id: '3', value: '24x your number of contacts' },
          ],
        },
      ],
    },
    {
      id: 'email',
      name: 'Email',
      features: [
        {
          id: 'email-dragDropEmailTemplates',
          feature: {
            name: 'Drag & drop email templates',
            tooltipLabel: 'Choose from hundreds of professionally designed, customizable templates or create your own.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-brandKit',
          feature: {
            name: 'BrandKit',
            tooltipLabel:
              'Input your website url and we’ll provide templates with your brand colors, logo, and images.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-aiContentRecommendations',
          feature: {
            name: 'AI content recommendations',
            tooltipLabel: 'Add a few keywords and we’ll provide you with content options to choose from.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-pollsSurveys',
          feature: {
            name: 'Polls & surveys',
            tooltipLabel:
              'Create and share a poll or survey to better understand your customers. Just add it to your email and watch the responses flow in.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          // PLAN-RESTRUCTURE: Lite unlocks scheduled email sends.
          id: 'email-scheduledEmailSends',
          feature: {
            name: 'Scheduled email sends',
            tooltipLabel: 'Send emails instantly or schedule for a future date and time, up to one year advance.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          // PLAN-RESTRUCTURE: A/B testing becomes 1/mo on Lite, unlimited on Standard/Premium.
          id: 'email-subjectLineABTesting',
          feature: {
            name: 'Subject line A/B testing',
            tooltipLabel:
              "Compare two subject lines with a split test to ensure you're always sending the most effective subject line.",
          },
          parameters: [
            { id: '1', value: '1/mo' },
            { id: '2', value: 'Unlimited' },
            { id: '3', value: 'Unlimited' },
          ],
        },
        {
          id: 'email-subjectLineRecommendations',
          feature: {
            name: 'Subject line recommendations',
            tooltipLabel:
              'Enhance email engagement by selecting suggested subject lines and preheaders. Our system scans content, optimizing for nonprofit, retail, or real estate audiences. Currently for emails in English only.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          // PLAN-RESTRUCTURE: Lite unlocks subject line personalization.
          id: 'email-subjectLinePersonalization',
          feature: {
            name: 'Subject line personalization',
            tooltipLabel:
              "Add contact details, like a first or last name, to your subject line. When your contacts read your subject line, they'll see details unique to them.",
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          // PLAN-RESTRUCTURE: Dynamic content blocks unlock on Lite and Standard.
          id: 'email-dynamicContentInEmail',
          feature: {
            name: 'Dynamic content in email',
            tooltipLabel:
              "Personalize your emails by having parts of your content vary based on a contact's city, state, job title or any data point you collect.",
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-reachAndEngagementReporting',
          feature: {
            name: 'Reach and engagement reporting',
            tooltipLabel: "Easily know what's working best by seeing views, clicks, bounces, and unsubscribes.",
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-drilldownReporting',
          feature: {
            name: 'Drilldown reporting',
            tooltipLabel:
              'Learn more about how your campaigns are performing by drilling into what links are being clicked, who is engaging, and more.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-engagementHeatMap',
          feature: {
            name: 'Engagement heat map',
            tooltipLabel: 'Know what’s working by seeing where your most clicked links are located.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
        {
          id: 'email-newsletterArchive',
          feature: {
            name: 'Newsletter archive',
            tooltipLabel:
              'Host links for up to 100 previously sent emails on your website using this embeddable widget.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          // PLAN-RESTRUCTURE: Inbox preview intentionally left as $10 add-on across all plans
          // (per Changelog 7/31: "removed inbox preview updates from test (no allocation in any plan)").
          id: 'email-inboxPreview',
          feature: {
            name: 'Inbox preview',
            tooltipLabel:
              'See how your email renders in various email applications so you can spot errors and make adjustments prior to hitting the send button.',
          },
          parameters: [
            { id: '1', value: '$10/month add-on' },
            { id: '2', value: '$10/month add-on' },
            { id: '3', value: '$10/month add-on' },
          ],
        },
      ],
    },
    {
      id: 'automation',
      name: 'Automation',
      features: [
        {
          // PLAN-RESTRUCTURE: Automation templates unlock on Lite and Standard (was 1 / 3 / unlimited).
          id: 'automation-automationsTemplates',
          feature: {
            name: 'Automation templates',
            tooltipLabel:
              'Library of pre-built templates for the most common automation needs. Includes Welcome Email, Birthday, Contact added to list, and more.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          // PLAN-RESTRUCTURE: Number of active automation paths capped 1 / 3 / unlimited.
          id: 'automation-activePaths',
          feature: {
            name: 'Active automation paths',
            tooltipLabel: 'Number of automation paths that can be running at the same time.',
          },
          parameters: [
            { id: '1', value: '1' },
            { id: '2', value: '3' },
            { id: '3', value: 'Unlimited' },
          ],
        },
        {
          // PLAN-RESTRUCTURE: Automation runs/month is displayed as a soft cap for
          // self-selection guidance; entitlement enforcement is not wired.
          id: 'automation-runsPerMonth',
          feature: {
            name: 'Automation runs per month',
            tooltipLabel: 'Number of automation steps that can be triggered each month.',
          },
          parameters: [
            { id: '1', value: '100' },
            { id: '2', value: '1,000' },
            { id: '3', value: 'Unlimited' },
          ],
        },
        {
          // PLAN-RESTRUCTURE: Resend-to-non-openers unlocks on Lite.
          id: 'automation-resendToNonOpeners',
          feature: {
            name: 'Resend to non-openers',
            tooltipLabel:
              'Increase open rates by choosing a timeframe to automatically resend an email to contacts who didn’t open yet.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'automation-ecommerceAutomationTemplates',
          feature: {
            name: 'Ecommerce automation templates',
            tooltipLabel:
              'Library of pre-built templates triggered by actions taken on your eCommerce site, such as Abandoned Shopping Cart and more.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
        {
          // PLAN-RESTRUCTURE: Custom automation paths unlock on Lite and Standard.
          id: 'automation-customAutomations',
          feature: {
            name: 'Custom automations',
            tooltipLabel: 'Build a custom automation from scratch to fit your unique needs.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
      ],
    },
    {
      id: 'socialMarketing',
      name: 'Social Marketing',
      features: [
        {
          id: 'socialMarketing-postOnFacebookInstagramLinkedIn',
          feature: {
            name: 'Post on Facebook, Instagram, LinkedIn',
            tooltipLabel:
              'Create and post on social media with our dashboard that automatically optimizes your copy and images for each platform.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'socialMarketing-postShortVideo',
          feature: {
            name: 'Post short video',
            tooltipLabel:
              'Upload and post short videos on Facebook, Instagram, and LinkedIn to boost engagement and expand reach',
          },
          parameters: [
            { id: '1', value: '5' },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'socialMarketing-socialReporting',
          feature: {
            name: 'Social reporting',
            tooltipLabel:
              'Get a sense of how your social posts are performing on Facebook or Instagram by viewing your overall reach and engagement activity for individual posts.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'socialMarketing-schedulePostsInAdvance',
          feature: {
            name: 'Schedule posts in advance',
            tooltipLabel: 'Create a post for multiple social platforms and schedule it to publish at a later time.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
      ],
    },
    {
      id: 'smsMarketing',
      name: 'SMS Marketing',
      features: [
        {
          id: 'smsMarketing-smsCampaigns',
          feature: {
            name: 'SMS campaigns',
            tooltipLabel: 'Reach customers directly, and immediately, on their mobile devices through text messages.',
          },
          parameters: [
            { id: '1', value: 'Starting at $10 USD/mo.' },
            { id: '2', value: 'Starting at $10 USD/mo.' },
            { id: '3', value: 'First 500 messages/mo. included*' },
          ],
        },
        {
          id: 'smsMarketing-textToJoin',
          feature: {
            name: 'Text-to-join',
            tooltipLabel:
              'Allow people to opt into your email and SMS list by sending a text message to a unique phone number assigned to your Constant Contact account.',
          },
          parameters: [
            { id: '1', value: 'Included with SMS marketing' },
            { id: '2', value: 'Included with SMS marketing' },
            { id: '3', value: true },
          ],
        },
        {
          id: 'smsMarketing-smsReporting',
          feature: {
            name: 'SMS reporting',
            tooltipLabel:
              'Check the number of sends, successful deliveries, bounces, unsubscribes, and the click rate for a sent SMS campaign.',
          },
          parameters: [
            { id: '1', value: 'Included with SMS marketing' },
            { id: '2', value: 'Included with SMS marketing' },
            { id: '3', value: true },
          ],
        },
      ],
      disclaimer: '*Standard SMS rates apply after your first 500 messages.',
      disclaimerLinkText: 'Standard SMS rates apply',
      disclaimerLinkUrl:
        'https://knowledgebase.constantcontact.com/email-digital-marketing/articles/KnowledgeBase/46517-Add-the-SMS-marketing-add-on-to-your-Constant-Contact-email-plan?lang=en_US',
    },
    {
      id: 'marketingCrm',
      name: 'Marketing CRM',
      features: [
        {
          id: 'marketingCrm-contactManagement',
          feature: {
            name: 'Contact management',
            tooltipLabel:
              'Add and manage contacts in your account. You can manually add, import a list, or create an automatic way a contact gets added to your list. \nEvery email includes a SafeUnsubscribe® link assuring your customers that you have used a legitimate permission-based email provider.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          // PLAN-RESTRUCTURE: 5,000-contact cap on Lite is displayed as a soft cap for
          // self-selection guidance; entitlement enforcement is not wired.
          id: 'marketingCrm-capOnContacts',
          feature: {
            name: 'Cap on contacts',
            tooltipLabel: 'Maximum number of contacts this plan supports.',
          },
          parameters: [
            { id: '1', value: '5,000' },
            { id: '2', value: 'No cap' },
            { id: '3', value: 'No cap' },
          ],
        },
        {
          id: 'marketingCrm-webSignUpForms',
          feature: {
            name: 'Web sign-up forms',
            tooltipLabel:
              'Turn visitors into leads with customizable forms and incentives to sign up like email promotions, a checklist, or a free consultation.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          // PLAN-RESTRUCTURE: Number of lists — new cap (10 / 25 / Unlimited).
          id: 'marketingCrm-numberOfLists',
          feature: {
            name: 'Number of lists',
            tooltipLabel: 'Group your contacts into lists to send targeted campaigns.',
          },
          parameters: [
            { id: '1', value: '10' },
            { id: '2', value: '25' },
            { id: '3', value: 'Unlimited' },
          ],
        },
        {
          // PLAN-RESTRUCTURE: Segment counts change from 1 / 10 to 2 / 5 (Standard drops).
          id: 'marketingCrm-contactSegmentation',
          feature: {
            name: 'Contact segmentation',
            tooltipLabel:
              'Get more engagement by creating customized segments with contact details, tags, and profiling information, then send personalized content to those who interacted with previous emails.',
          },
          parameters: [
            { id: '1', value: '2' },
            { id: '2', value: '5' },
            { id: '3', value: 'Unlimited' },
          ],
        },
        {
          // PLAN-RESTRUCTURE: Custom static segmentation unlocks on Lite (per Vanessa's 2026-08-04
          // update to the source-of-truth sheet — Lite already had access in production).
          id: 'marketingCrm-customStaticSegmentation',
          feature: {
            name: 'Custom static segmentation',
            tooltipLabel: 'Build segments from list membership, tags, or contact details.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          // PLAN-RESTRUCTURE: Contact activity segmentation unlocks on Lite.
          id: 'marketingCrm-engagementSegmentation',
          feature: {
            name: 'Engagement segmentation',
            tooltipLabel: 'Identify subscribers most likely to engage with future campaigns.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          // PLAN-RESTRUCTURE: eComm custom segmentation unlocks on Lite and Standard.
          id: 'marketingCrm-ecommerceSegmentation',
          feature: {
            name: 'Ecommerce segmentation',
            tooltipLabel: 'Send targeted content based on ecommerce activity.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
      ],
    },
    {
      id: 'ads',
      name: 'Ads',
      features: [
        {
          id: 'ads-socialMediaAdsManager',
          feature: {
            name: 'Social media ads manager',
            tooltipLabel:
              'Boost awareness and traffic for your business with our streamlined experience that displays ads in your target customer’s Facebook and/or Instagram feeds.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'ads-facebookLookalikeAdTargeting',
          feature: {
            name: 'Facebook Lookalike Ad targeting',
            tooltipLabel:
              'Reach the right prospects by targeting people who look like your current customers or a specific segment.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
        {
          id: 'ads-googleAdsManager',
          feature: {
            name: 'Google Ads Manager',
            tooltipLabel:
              'Our streamlined experience automates ad setup and management so you get the most clicks for the lowest cost.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
        {
          id: 'ads-googleAdsCredit',
          feature: {
            name: '$500 Google Ads credit',
            tooltipLabel:
              'Spend $500 on Google Ads in your first 60 days and get an additional $500 in ad credit. New US accounts only.',
            tooltipLinkText: 'Terms and conditions apply',
            tooltipLinkUrl: 'https://www.google.com/ads/coupons/terms/',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
      ],
    },
    {
      id: 'events',
      name: 'Events',
      features: [
        {
          id: 'events-registrationForm',
          feature: {
            name: 'Registration form',
            tooltipLabel: 'Choose which information to collect from your contacts when they register for your event.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'events-payments',
          feature: {
            name: 'Payments',
            tooltipLabel:
              'Enable tickets, add promo codes, and set up payment methods as part of the registration and payment process.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'events-sellProductsServices',
          feature: {
            name: 'Sell products & services',
            tooltipLabel:
              "If you're charging for event services or selling merch, you can collect payment during the registration process.",
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
      ],
    },
    {
      id: 'additionalFeatures',
      name: 'Additional Features',
      features: [
        {
          id: 'additionalFeatures-marketingCampaignCalendar',
          feature: {
            name: 'Marketing campaign calendar',
            tooltipLabel: 'Stay organized and efficient with all your marketing activities mapped in one view.',
          },
          parameters: [
            { id: '1', value: true },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          // PLAN-RESTRUCTURE: Total integrations cap on Lite (was uncapped in default).
          id: 'additionalFeatures-totalIntegrations',
          feature: {
            name: 'Total integrations',
            tooltipLabel:
              'Automatically sync contact and engagement data to save time, target your messaging, and deliver more successful campaigns.',
          },
          parameters: [
            { id: '1', value: '3' },
            { id: '2', value: 'Unlimited' },
            { id: '3', value: 'Unlimited' },
          ],
        },
        {
          // PLAN-RESTRUCTURE: Enterprise integrations exclusive to Premium.
          id: 'additionalFeatures-enterpriseIntegrations',
          feature: {
            name: 'Enterprise integrations (Salesforce, MS Dynamics)',
            tooltipLabel: 'Connect enterprise CRMs like Salesforce and Microsoft Dynamics.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
        {
          id: 'additionalFeatures-campaignBuilder',
          feature: {
            name: 'Campaign Builder',
            tooltipLabel: 'Tell us your goal and get a multi-channel campaign made for you in minutes.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: true },
            { id: '3', value: true },
          ],
        },
        {
          id: 'additionalFeatures-revenueReporting',
          feature: {
            name: 'Revenue reporting',
            tooltipLabel:
              'Connect an online store like Shopify or Etsy to enhance your dashboard reporting with revenue, conversions, and sales.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
        {
          id: 'additionalFeatures-seoRecommendations',
          feature: {
            name: 'SEO recommendations',
            tooltipLabel:
              'Get personalized step-by-step recommendations to boost your rankings in search results and drive more traffic to your website.',
          },
          parameters: [
            { id: '1', value: false },
            { id: '2', value: false },
            { id: '3', value: true },
          ],
        },
      ],
    },
  ].map((group) => ({
    ...group,
    features: group.features.map((feature) => ({
      ...feature,
      parameters: [...feature.parameters].reverse().reverse(),
    })),
  })),
};
